import { Injectable, Injector } from '@angular/core';
import { BrowserControlService } from '../../../../cross-team-shared/misc-services/browser-control.service';
import { Modifier } from '../../../../cross-team-shared/misc-services/events/event-helper';
import {
  MouseEventInfo,
  ShapeEventInfo,
  SubpointEventInfo,
} from '../../../../cross-team-shared/misc-services/events/mouse-event.service';
import { selectionToArray } from '../../../../cross-team-shared/misc-utils';
import { ToolName } from '../../../../cross-team-shared/tools-names';
import { VectorTaggingShapeType } from '../../../../team-apis/otterdam/answers/shape-typings';
import { getShapeHelper } from '../../shape-helpers/get-shape-helper';
import { SubpointSelectionService } from '../../utils/subpoint-selection.service';
import { AbstractTool2D } from '../shared/abstract-tool-2d';

@Injectable()
export class SelectTool2D extends AbstractTool2D {
  readonly name: ToolName = 'Select';

  protected subpointSelectionService: SubpointSelectionService;

  protected lastMouseDownEventFromShapeOrShapePart?: MouseEvent;

  constructor(injector: Injector) {
    super(injector);
    this.browserControlService = injector.get(BrowserControlService);
    this.subpointSelectionService = injector.get(SubpointSelectionService);
  }

  override async onActivate() {
    await super.onActivate();
    this.store('editMode').set('select');
    this.onEvent('container', 'mousedown', this.onContainerMouseDown);
    this.onEvent('shape', 'dblclick', this.onShapeDoubleClick);
    const modifiersToTrack: Modifier[][] = [[], ['platform']];
    for (const modifiers of modifiersToTrack) {
      this.onEvent('shape', 'mousedown', this.onShapeMouseDown, { modifiers });
      this.onEvent('subpoint', 'mousedown', this.onSubpointMouseDown, {
        modifiers,
      });
    }
    this.onEvent('subpoint', 'mousedown', this.onSubpointMouseDown, {
      modifiers: ['altKey'],
    });
  }

  override onDeactivate() {
    super.onDeactivate();
    this.store('editMode').set('none');
  }

  protected onShapeMouseDown(
    event: MouseEvent,
    { shapeStore }: ShapeEventInfo,
    source = 'select_tool_2d',
  ) {
    this.lastMouseDownEventFromShapeOrShapePart = event;
    this.modifySelection(shapeStore, event, source);
  }

  protected onShapeDoubleClick(
    _event: MouseEvent,
    { shapeStore }: ShapeEventInfo,
  ) {
    this.shellConnector.scopeObject(shapeStore.state().index);
  }

  protected onSubpointMouseDown(
    event: MouseEvent,
    eventInfo: SubpointEventInfo,
    source = 'select_tool_2d',
  ) {
    this.lastMouseDownEventFromShapeOrShapePart = event;
    this.modifySubpointSelection(eventInfo, event, source);
  }

  protected onContainerMouseDown(
    event: MouseEvent,
    _eventInfo: MouseEventInfo,
    source = 'select_tool_2d',
  ) {
    if (event !== this.lastMouseDownEventFromShapeOrShapePart) {
      this.subpointSelectionService.setSubpointSelectionWithoutTracking({});
      this.shellConnector.modifyShapeSelection([], 'set', { source });
    }
  }

  protected allowsSubpointSelection(shapeType: VectorTaggingShapeType) {
    return getShapeHelper(shapeType).allowsSubpointSelection;
  }

  private modifySubpointSelection(
    eventInfo: SubpointEventInfo,
    event: MouseEvent,
    source = 'select_tool_2d',
  ) {
    const shape = eventInfo.shapeStore.state();
    const modifierDown =
      event[this.browserControlService.getPlatformModifier()];
    const selectMode = modifierDown ? 'toggle' : 'set';

    if (this.allowsSubpointSelection(shape.type)) {
      this.subpointSelectionService.modifySubpointSelection(
        eventInfo,
        selectMode,
        source,
      );
      this.shellConnector.modifyShapeSelection(
        selectionToArray(this.store.state().selection),
        'set',
        { source },
      );
    } else {
      this.shellConnector.modifyShapeSelection([shape.index], selectMode, {
        source,
      });
    }
  }
}
